import React from "react";
import { ArrowDown, MessageCircle, Phone } from "lucide-react";
import { useLang } from "../LanguageContext";
import { RESTAURANT, telLink, waLink } from "../restaurant.config";

const HERO_IMG = "/photos/hero-dehors-piazza.jpg";

export default function Hero() {
  const { t } = useLang();

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden bg-[#23211E] text-[#F5F2EC]"
    >
      {/* Background image */}
      <div
        className="absolute inset-0 bg-cover bg-center scale-105 hero-ken-burns"
        style={{ backgroundImage: `url(${HERO_IMG})` }}
        aria-hidden="true"
      />
      {/* Subtle dark overlay (guidelines: not more than black/40) */}
      <div className="absolute inset-0 bg-black/40" aria-hidden="true" />
      {/* Vertical gradient for legibility at the top & bottom */}
      <div
        className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/60"
        aria-hidden="true"
      />
      {/* Grain */}
      <div className="absolute inset-0 opacity-[0.12] mix-blend-overlay grain-overlay" aria-hidden="true" />

      {/* Content */}
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 min-h-screen flex flex-col justify-end pb-16 md:pb-24 pt-28">
        <div className="grid grid-cols-12 gap-6 md:gap-10 items-end">
          <div className="col-span-12 md:col-span-10 lg:col-span-9">
            <p
              data-testid="hero-eyebrow"
              className="text-[11px] md:text-xs uppercase tracking-[0.35em] mb-8 opacity-90 reveal-up"
            >
              {t.hero.eyebrow}
            </p>
            <h1
              data-testid="hero-title"
              className="font-serif font-light tracking-tighter leading-[0.88] text-6xl sm:text-7xl md:text-[8rem] lg:text-[11rem] reveal-up"
              style={{ animationDelay: "120ms" }}
            >
              <span className="block opacity-90 text-3xl sm:text-4xl md:text-5xl italic font-normal tracking-wide mb-2 md:mb-4">
                {t.hero.titleLine1}
              </span>
              <span className="block">{t.hero.titleLine2}</span>
              <span className="block italic text-[#E4B363]">
                {t.hero.titleLine3}
              </span>
            </h1>
          </div>

          <div className="col-span-12 md:col-span-8 lg:col-span-6 md:col-start-5 lg:col-start-7">
            <p
              data-testid="hero-subtitle"
              className="text-base md:text-lg font-light leading-relaxed text-[#F5F2EC]/90 mb-10 reveal-up"
              style={{ animationDelay: "260ms" }}
            >
              {t.hero.subtitle}
            </p>

            <div
              className="flex flex-wrap gap-3 md:gap-4 reveal-up"
              style={{ animationDelay: "380ms" }}
            >
              <a
                data-testid="hero-whatsapp-btn"
                href={waLink(t.contatti.prefill)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3.5 bg-[#B85532] text-white text-xs uppercase tracking-[0.22em] hover:-translate-y-0.5 hover:bg-[#a04825] transition-all"
              >
                <MessageCircle className="w-4 h-4" strokeWidth={1.5} />
                {t.hero.ctaReserve}
              </a>
              <a
                data-testid="hero-call-btn"
                href={telLink("mobile")}
                className="inline-flex items-center gap-2 px-6 py-3.5 border border-[#F5F2EC] text-[#F5F2EC] text-xs uppercase tracking-[0.22em] hover:bg-[#F5F2EC] hover:text-[#23211E] transition-all"
              >
                <Phone className="w-4 h-4" strokeWidth={1.5} />
                {t.hero.ctaCall} · {RESTAURANT.mobileDisplay}
              </a>
            </div>
          </div>
        </div>

        {/* Scroll hint */}
        <div className="mt-16 md:mt-24 flex items-center gap-3 opacity-70">
          <span className="text-[10px] uppercase tracking-[0.4em]">
            {t.hero.scroll}
          </span>
          <ArrowDown className="w-4 h-4 animate-bounce-slow" strokeWidth={1.2} />
        </div>
      </div>
    </section>
  );
}
